import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getStackTrace, log } from '@src/Log.init';
import { retrieveNotifications } from 'features/notification/notificationSlice';
import {
  retrieveBaseRoles,
  retrievePermissions,
  retrieveRoles,
} from 'features/role/roleSlice';
import { getCurrencyFormat, reconstituteCurrency } from 'helpers/currency';
import dashboardDates from 'views/Dashboard/dashboardDates.const';
import {
  getReceipt,
  getSession,
  searchTransactions,
} from '../../api/sessionAPI';
import { tenantApi } from '../../api/tenant.api';
import {
  retrieveClients,
  retrieveClientTenants,
  retrieveTenantClient,
} from '../client/clientSlice';

export const initializeAdministrativeSession = createAsyncThunk(
  'session/initializeAdministrativeSession',
  async (token, { getState, dispatch }) => {
    const response = await getSession(token);
    await dispatch(setToken(response.token));
    await dispatch(retrievePermissions());
    await dispatch(retrieveBaseRoles());
    await dispatch(retrieveClients());
    return response;
  },
);

export const initializeImpersonationSession = createAsyncThunk(
  'session/initializeImpersonationSession',
  async (token, { getState, dispatch }) => {
    const period = dashboardDates[0].replace(/\s+/g, '').toLowerCase();
    const response = await getSession(token);
    await dispatch(setToken(response.token));
    await dispatch(tenantApi.endpoints.getTenant.initiate()).unwrap();
    await dispatch(tenantApi.util.invalidateTags(['Tenant', period]));
    await dispatch(
      tenantApi.endpoints.getTenantStats.initiate({ period }),
    ).unwrap();
    await dispatch(retrieveRoles());
    await dispatch(retrieveNotifications());
    return response;
  },
);

export const exitImpersonationSession = createAsyncThunk(
  'session/exitImpersonationSession',
  async (token, { getState, dispatch }) => {
    const response = await getSession(token);
    await dispatch(setToken(response.token));

    return response;
  },
);

export const initializeTenantSession = createAsyncThunk(
  'session/initializeTenantSession',
  async (token, { getState, dispatch }) => {
    const period = dashboardDates[0].replace(/\s+/g, '').toLowerCase();
    const response = await getSession(token);
    await dispatch(setToken(response.token));
    await dispatch(tenantApi.endpoints.getTenant.initiate()).unwrap();
    await dispatch(
      tenantApi.endpoints.getTenantStats.initiate({ period }),
    ).unwrap();
    await dispatch(retrieveTenantClient());
    await dispatch(retrieveClientTenants());
    const client = getState().client.currentClient;
    await dispatch(setSessionClient(client));
    // base requests
    await dispatch(retrievePermissions());
    await dispatch(retrieveBaseRoles());
    await dispatch(retrieveParameterDictionary());
    await dispatch(retrieveTemplateDictionary());
    // tenant specific requests
    await dispatch(retrieveRoles());
    await dispatch(retrieveParameterLists());
    await dispatch(retrieveTemplateSets());
    await dispatch(retrieveNotifications());

    return response;
  },
);

export const getTransactions = createAsyncThunk(
  'session/getTransactions',
  async (searchCriteria, { getState, dispatch }) => {
    const transactions = await searchTransactions(
      getState().session.sessionToken,
      searchCriteria,
    );
    return transactions;
  },
);

export const convertCurrencyToDinero = (data) => {
  // Handle null or undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => convertCurrencyToDinero(item));
  }

  // Handle objects
  if (typeof data === 'object') {
    // Check if current object is a currency object
    if (
      'amount' in data &&
      'currency' in data &&
      'precision' in data &&
      typeof data.amount === 'number' &&
      typeof data.currency === 'string' &&
      typeof data.precision === 'number'
    ) {
      return reconstituteCurrency(data.amount).toFormat(
        getCurrencyFormat(data.currency),
      );
    }

    // Recursively process all object properties
    const result = {};
    for (const [key, value] of Object.entries(data)) {
      result[key] = convertCurrencyToDinero(value);
    }
    return result;
  }

  // Return primitive values as is
  return data;
};

export const retrieveReceipt = createAsyncThunk(
  'session/retrieveReceipt',
  async (sessionId, { getState, dispatch }) => {
    const receipt = await getReceipt(
      getState().session.sessionToken,
      sessionId,
    );
    const convertedReceipt = convertCurrencyToDinero(receipt);
    return convertedReceipt;
  },
);

const populateSessionData = (state, action) => {
  const session = action.payload;
  state.clientID = session.clientId;
  state.tenantID = session.tenantId;
  state.locale = session.locale;
  state.userID = session.userId;
  state.userName = session.userName;
  state.sessionStatus = 'active';
  state.impersonationStatus = false;
  state.client = null;
  state.tenant = null;
};

const populateTenantSessionData = (state, action) => {
  const session = action.payload;
  state.clientID = session.clientId;
  state.tenantID = session.tenantId;
  state.locale = session.locale;
  state.userID = session.userId;
  state.userName = session.userName;
  state.sessionStatus = 'active';
};

const populateImpersonationSessionData = (state, action) => {
  const session = action.payload;
  state.clientID = session.clientId;
  state.tenantID = session.tenantId;
  state.locale = session.locale;
  state.userID = session.userId;
  state.userName = session.userName;
  state.impersonationStatus = 'active';
};

const populateSearchResults = (state, action) => {
  state.resultCount = action.payload[0].metadata[0].total;
  state.searchResults = action.payload[0].data;
  state.searchResults.map((transaction) => {
    transaction.total_gross = transaction.total_gross
      ? reconstituteCurrency(transaction.total_gross)
      : null;
    transaction.total_net = transaction.total_net
      ? reconstituteCurrency(transaction.total_net)
      : null;
    transaction.total_tax = transaction.total_tax
      ? reconstituteCurrency(transaction.total_tax)
      : null;
  });
  state.searchStatus = 'complete';
};

const populateCurrentReceipt = (state, action) => {
  state.currentReceipt = action.payload;
  state.receiptStatus = 'success';
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState: {
    sessionStatus: 'inactive',
    impersonationStatus: 'inactive',
    clientID: null,
    sessionToken: null,
    accessToken: null,
    tenantID: null,
    directoryID: null,
    userID: null,
    userName: null,
    locale: null,
    impersonationMode: false,
    client: null,
    tenant: null,
    searchStatus: 'inactive',
    searchResults: null,
    resultCount: 0,
    pageLimit: 10,
    pageNumber: 1,
    currentReceipt: null,
    receiptStatus: null,
    ntAdministrator: false,
  },
  reducers: {
    setToken: (state, action) => {
      state.sessionToken = action.payload;
    },
    setAccessToken: (state, action) => {
      state.accessToken = action.payload;
    },
    setDirectoryID: (state, action) => {
      state.directoryID = action.payload;
    },
    setSessionData: (state, action) => {
      state.tenantID = action.payload.tenant_id;
      state.clientID = action.payload.client_id;
      state.locale = action.payload.locale;
      state.userID = action.payload.user_id;
      state.userName = action.payload.userName;
      state.sessionStatus = 'active';
      state.impersonationStatus = false;
      state.client = null;
      state.tenant = null;
    },
    setTenantSessionData: (state, action) => {
      state.tenantID = action.payload.tenant_id;
      state.clientID = action.payload.client_id;
      state.locale = action.payload.locale;
      state.userID = action.payload.user_id;
      state.userName = action.payload.userName;
      state.sessionStatus = 'active';
    },
    setImpersonationSessionData: (state, action) => {
      state.tenantID = action.payload.tenant_id;
      state.clientID = action.payload.client_id;
      state.locale = action.payload.locale;
      state.userID = action.payload.user_id;
      state.userName = action.payload.userName;
      state.impersonationStatus = false;
    },
    setImpersonationMode: (state, action) => {
      // if this user is an ntAdministrator, then set the current client and a default tenant
      state.impersonationMode = action.payload;
    },
    setNTAdministrator: (state, action) => {
      state.ntAdministrator = action.payload;
    },
    setImpersonatedClient: (state, action) => {
      const client = action.payload;
      state.client = client;
      state.clientID = client._id;
    },
    setImpersonatedTenant: (state, action) => {
      const tenant = action.payload;
      state.tenant = tenant;
      state.tenantID = tenant?.tenant_id;
    },
    setSessionTenant: (state, action) => {
      const tenant = action.payload;
      state.tenant = tenant;
      state.tenantID = tenant.tenant_id;
    },
    setSessionClient: (state, action) => {
      const client = action.payload;
      state.client = client;
      state.clientID = client._id;
    },
    setResultPageLimit: (state, action) => {
      state.pageLimit = action.payload;
    },
    resetSearchResults: (state, action) => {
      state.searchStatus = 'inactive';
      state.searchResults = null;
      state.currentReceipt = null;
      state.receiptStatus = null;
    },
  },
  extraReducers: {
    [initializeAdministrativeSession.pending]: (state, action) => {
      state.sessionStatus = 'pending';
      log.debug({
        message_type: 'Session',
        message: 'initializing session...',
      });
    },
    [initializeAdministrativeSession.fulfilled]: (state, action) => {
      populateSessionData(state, action);
      log.info({
        message_type: 'Session',
        message: 'session initialization complete!',
      });
    },
    [initializeAdministrativeSession.rejected]: (state, action) => {
      state.sessionStatus = 'failed';
      log.error({
        message_type: 'SystemError',
        message: 'session initialization failed!',
        meta_data: getStackTrace(),
      });
    },
    [initializeImpersonationSession.pending]: (state, action) => {
      state.impersonationStatus = 'pending';
      log.debug({
        message_type: 'Session',
        message: 'Impersonation: initializing session...',
      });
    },
    [initializeImpersonationSession.fulfilled]: (state, action) => {
      populateImpersonationSessionData(state, action);
      log.info({
        message_type: 'Session',
        message: 'Impersonation: session initialization complete!',
      });
    },
    [initializeImpersonationSession.rejected]: (state, action) => {
      state.impersonationStatus = 'failed';
      log.error({
        message_type: 'SystemError',
        message: 'Impersonation: session initialization failed!',
        meta_data: getStackTrace(),
      });
    },
    [exitImpersonationSession.pending]: (state, action) => {
      state.sessionStatus = 'pending';
      log.debug({
        message_type: 'Session',
        message: 'Exit Impersonation: initializing session...',
      });
    },
    [exitImpersonationSession.fulfilled]: (state, action) => {
      populateSessionData(state, action);
      log.info({
        message_type: 'Session',
        message: 'Exit Impersonation: session initialization complete!',
      });
    },
    [exitImpersonationSession.rejected]: (state, action) => {
      state.sessionStatus = 'failed';
      log.error({
        message_type: 'SystemError',
        message: 'Exit Impersonation: session initialization failed!',
        meta_data: getStackTrace(),
      });
    },
    [initializeTenantSession.pending]: (state, action) => {
      state.sessionStatus = 'pending';
      log.debug({
        message_type: 'Session',
        message: 'Tenant: initializing session...',
      });
    },
    [initializeTenantSession.fulfilled]: (state, action) => {
      populateTenantSessionData(state, action);
      log.info({
        message_type: 'Session',
        message: 'Tenant: session initialization complete!',
      });
    },
    [initializeTenantSession.rejected]: (state, action) => {
      state.sessionStatus = 'failed';
      log.error({
        message_type: 'SystemError',
        message: 'Tenant: session initialization failed!',
        meta_data: getStackTrace(),
      });
    },
    [getTransactions.pending]: (state, action) => {
      state.searchStatus = 'pending';
      log.debug({
        message_type: 'Session',
        message: 'searching transactions...',
      });
    },
    [getTransactions.fulfilled]: (state, action) => {
      populateSearchResults(state, action);
      log.info({
        message_type: 'Session',
        message: 'transaction search complete!',
      });
    },
    [getTransactions.rejected]: (state, action) => {
      state.searchStatus = 'failed';
      log.error({
        message_type: 'SystemError',
        message: 'transaction search failed!',
        meta_data: getStackTrace(),
      });
    },
    [retrieveReceipt.pending]: (state, action) => {
      state.receiptStatus = 'pending';
      log.debug({
        message_type: 'Session',
        message: 'retrieving transaction receipt...',
      });
    },
    [retrieveReceipt.fulfilled]: (state, action) => {
      populateCurrentReceipt(state, action);
      log.info({
        message_type: 'Session',
        message: 'transaction receipt retrieval complete!',
      });
    },
    [retrieveReceipt.rejected]: (state, action) => {
      state.receiptStatus = 'failed';
      log.error({
        message_type: 'SystemError',
        message: 'transaction receipt retrieval failed!',
        meta_data: getStackTrace(),
      });
    },
  },
});

export const {
  setToken,
  setAccessToken,
  setDirectoryID,
  setSessionData,
  setTenantSessionData,
  setImpersonationSessionData,
  setImpersonationMode,
  setImpersonatedClient,
  setImpersonatedTenant,
  setSessionTenant,
  setSessionClient,
  setResultPageLimit,
  resetSearchResults,
  setNTAdministrator,
} = sessionSlice.actions;

export const selectSessionToken = (state) => state.session.sessionToken;
export const selectUserId = (state) => state.session.userID;
export const selectAccessToken = (state) => state.session.accessToken;
export const selectRedirectURL = (state) => state.session.redirectURL;
export const selectLocale = (state) => state.session.locale;
export const selectSessionStatus = (state) => state.session.sessionStatus;
export const selectImpersonationStatus = (state) =>
  state.session.impersonationStatus;
export const selectNTadministrator = (state) => state.session.ntAdministrator;
export const selectImpersonationMode = (state) =>
  state.session.impersonationMode;
export const selectClient = (state) => state.session.client;
export const selectClientTenant = (state) => state.session.tenant;
export const selectSearchStatus = (state) => state.session.searchStatus;
export const selectSearchResults = (state) => state.session.searchResults;
export const selectReceiptStatus = (state) => state.session.receiptStatus;
export const selectCurrentReceipt = (state) => state.session.currentReceipt;
export const selectResultPages = (state) =>
  state.session.resultCount / state.session.pageLimit;
export const selectResultCount = (state) => state.session.resultCount;

export default sessionSlice.reducer;
